import { createStore, useStore, type ReactStore } from '@w/react-store'
import { disposeAll } from '@w/utils'

/** A signed-in user's resources; `close` releases them. */
export interface SessionResource {
  readonly userId: string
  close(): Promise<void>
}

/**
 * Owner of the signed-in user's session.
 *
 * INV-1: a session stays open while its holder, the protected view that renders it, is mounted.
 * INV-2: one holder at a time. Supported topology: one app root per owner, whose router mounts only
 *   the active activity, so at most one protected boundary is committed. Several roots or
 *   simultaneously mounted boundaries sharing an owner are not supported.
 * INV-3: at most one published session; `open` requires the previous one retired, so teardown
 *   never loses a session.
 *
 * - `open`: publishes the session that `useSession` renders; throws while another is published.
 * - `hold`: the committed protected view keeps the session open; throws on a second
 *   holder (INV-2), since the router mounts only the active activity. Leaving and holding
 *   again before retirement (StrictMode replay, route transitions) keeps it open.
 * - `retire`: sign-out and user switch unpublish the session now and release it
 *   once its holder left. Release closes the session, then runs `cleanups`; every
 *   step runs and failures chain in disposal order (`disposeAll`), then go to
 *   `reportReleaseError`.
 * - `closeAll`: app teardown; releases everything and awaits releases already
 *   in flight.
 */
export interface SessionOwner<R extends SessionResource> {
  open(session: R): void
  retire(...cleanups: (() => unknown)[]): Promise<void>
  hold(session: R): () => void
  closeAll(): Promise<void>
}

const storeKey = Symbol('session store')
type StoredOwner<R extends SessionResource> = SessionOwner<R> & { [storeKey]: ReactStore<R | null> }

export function createSessionOwner<R extends SessionResource>(options: {
  reportReleaseError(error: unknown, session: R): void
}): SessionOwner<R> {
  const current = createStore<R | null>(null)
  const retired = new Map<R, { cleanups: (() => unknown)[]; closing?: Promise<void> }>()
  let holder: R | undefined

  function release(session: R, entry: { cleanups: (() => unknown)[]; closing?: Promise<void> }) {
    entry.closing ??= disposeAll([() => session.close(), ...entry.cleanups], 'Session release failed')
      .catch((error: unknown) => { options.reportReleaseError(error, session) })
      .finally(() => { retired.delete(session) })
    return entry.closing
  }

  const owner: StoredOwner<R> = {
    [storeKey]: current,
    open(session) {
      if (current.getState()) throw new Error('INV: retire the current session before opening another')
      current.update(session)
    },
    retire(...cleanups) {
      const session = current.getState()
      if (!session) return Promise.resolve()
      current.update(null)
      const entry = { cleanups }
      retired.set(session, entry)
      return holder === session ? Promise.resolve() : release(session, entry)
    },
    hold(session) {
      if (holder && holder !== session) throw new Error('INV-2: one holder at a time')
      holder = session
      return () => {
        holder = undefined
        const entry = retired.get(session)
        if (entry) void release(session, entry)
      }
    },
    async closeAll() {
      const session = current.getState()
      if (session && !retired.has(session)) retired.set(session, { cleanups: [] })
      current.update(null)
      await Promise.all([...retired].map(([value, entry]) => release(value, entry)))
    },
  }
  return owner
}

/** The published session; re-renders when it opens or retires. */
export function useSession<R extends SessionResource>(owner: SessionOwner<R>): R | null {
  return useStore((owner as StoredOwner<R>)[storeKey])
}
